/**
 * Die beiden Formulare der Mitarbeiterseite: Backoffice-Zugang und neuer
 * Mitarbeiter.
 *
 * Eigene Datei, damit die Seite selbst kurz bleibt (HJ-010). Neu gegenüber
 * vorher sind nur die Knopftexte — sie heißen jetzt wie die Knöpfe in der
 * Zeile, damit man im Formular wiedererkennt, was man angeklickt hat — und
 * die Rollenwahl beim Anlegen: nur Fahrer und Büro (HJ-015). Admin wird man
 * danach über „Rolle", durch einen Admin, mit Protokoll.
 */

import { useState } from 'react';
import { Fehlerkasten, Karte, Knopf, Meldung, Textfeld } from '../gestaltung/bausteine';
import { Speicherfehler } from '../daten/speichern';
import { alsKlarfehler, type Klarfehler } from '../daten/fehler';
import {
  ANLEGBARE_ROLLEN,
  mitarbeiterAnlegen,
  ROLLENTEXT,
  type Mitarbeiterzeile,
} from '../daten/mitarbeiter';
import { useEntwurfsfeld } from '../daten/formularEntwurf';

/* ------------------------------------------------------------ Zugangsformular */

/**
 * Anlegen fragt nach der Adresse, Zurücksetzen fragt nach — nichts, aber es
 * fragt. Ein Zurücksetzen ohne Rückfrage wäre ein Knopf, der einen Kollegen
 * aussperrt, wenn man ihn in der falschen Zeile trifft.
 */
export function ZugangFormular(eigenschaften: {
  person: Mitarbeiterzeile;
  art: 'anlegen' | 'passwort_neu';
  laeuft: boolean;
  abbrechen: () => void;
  bestaetigen: (email?: string) => void;
}) {
  const { person, art } = eigenschaften;
  const [email, setEmail] = useState(person.email ?? '');
  const name = `${person.vorname} ${person.nachname}`;
  const gueltig = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());

  return (
    <Karte
      titel={art === 'anlegen' ? `Backoffice-Zugang für ${name}` : `Neues Passwort für ${name}`}
    >
      <div className="stapel" style={{ maxWidth: 520 }}>
        {art === 'anlegen' ? (
          <>
            <p className="meta">
              {name} kann sich danach mit dieser Adresse und einem
              Einmalpasswort anmelden und muss beim ersten Mal ein eigenes
              Passwort vergeben.
            </p>
            <Textfeld
              marke="E-Mail-Adresse"
              wert={email}
              setzen={setEmail}
              art="email"
              autoFocus
              disabled={eigenschaften.laeuft}
              hinweis="Am besten eine Firmenadresse — sie ist der Anmeldename und lässt sich später ändern."
            />
          </>
        ) : (
          <Meldung art="warnung" titel="Das bisherige Passwort gilt danach nicht mehr">
            {name} kann sich mit dem alten Passwort nicht mehr anmelden und
            braucht das neue. Der Vorgang wird protokolliert.
          </Meldung>
        )}

        <div className="reihe reihe--rechts">
          <Knopf art="still" onClick={eigenschaften.abbrechen}>
            Abbrechen
          </Knopf>
          <Knopf
            art="haupt"
            disabled={eigenschaften.laeuft || (art === 'anlegen' && !gueltig)}
            onClick={() =>
              eigenschaften.bestaetigen(
                art === 'anlegen' ? email.trim() : undefined,
              )
            }
          >
            {eigenschaften.laeuft
              ? 'Einen Moment …'
              : art === 'anlegen'
                ? 'Backoffice-Zugang anlegen'
                : 'Neues Passwort vergeben'}
          </Knopf>
        </div>
      </div>
    </Karte>
  );
}

/* --------------------------------------------------------------- Formular */

export function AnlegenFormular(eigenschaften: { fertig: () => void }) {
  const [vorname, setVorname] = useEntwurfsfeld('mitarbeiter-neu', 'vorname', '');
  const [nachname, setNachname] = useEntwurfsfeld('mitarbeiter-neu', 'nachname', '');
  const [rolle, setRolle] = useEntwurfsfeld<(typeof ANLEGBARE_ROLLEN)[number]>(
    'mitarbeiter-neu',
    'rolle',
    'fahrer',
  );
  const [personalnummer, setPersonalnummer] = useEntwurfsfeld(
    'mitarbeiter-neu',
    'personalnummer',
    '',
  );
  const [telefon, setTelefon] = useEntwurfsfeld('mitarbeiter-neu', 'telefon', '');
  const [istTestkonto, setIstTestkonto] = useEntwurfsfeld('mitarbeiter-neu', 'istTestkonto', false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Klarfehler | null>(null);

  const vollstaendig = vorname.trim() !== '' && nachname.trim() !== '';

  async function speichern() {
    if (!vollstaendig || laeuft) return;
    setLaeuft(true);
    setFehler(null);
    try {
      await mitarbeiterAnlegen({
        vorname,
        nachname,
        rolle,
        personalnummer,
        telefon,
        istTestkonto,
      });
      eigenschaften.fertig();
    } catch (problem) {
      setFehler(
        problem instanceof Speicherfehler ? problem.klar : alsKlarfehler(problem),
      );
      setLaeuft(false);
    }
  }

  return (
    <Karte titel="Neuer Mitarbeiter">
      <div className="stapel">
        {fehler && <Fehlerkasten fehler={fehler} />}

        <div className="feldreihe">
          <Textfeld marke="Vorname" wert={vorname} setzen={setVorname} autoFocus />
          <Textfeld marke="Nachname" wert={nachname} setzen={setNachname} />
          <label className="feld">
            <span className="feld__marke">Rolle</span>
            <select
              value={rolle}
              onChange={(e) => setRolle(e.target.value as typeof rolle)}
            >
              {ANLEGBARE_ROLLEN.map((r) => (
                <option key={r} value={r}>
                  {ROLLENTEXT[r]}
                </option>
              ))}
            </select>
            <span className="feld__hinweis">
              Fahrer richten die App mit einem Code ein, Büro bekommt E-Mail
              und Passwort. Admin wird man danach über „Rolle".
            </span>
          </label>
        </div>

        <div className="feldreihe">
          <Textfeld
            marke="Personalnummer"
            wert={personalnummer}
            setzen={setPersonalnummer}
            hinweis="Optional — für die Lohnabrechnung."
          />
          <Textfeld
            marke="Telefon"
            wert={telefon}
            setzen={setTelefon}
            hinweis="Optional. Hier landet später der Einrichtungscode per SMS."
          />
        </div>

        {/* Ein Testkonto erzeugt echte Fahrten, Arbeitstage und Spesentage.
            Ohne diese Kennzeichnung fällt bei der ersten Monatsauswertung
            Verpflegungsmehraufwand für jemanden an, der nie unterwegs war. */}
        <label className="kaestchen">
          <input
            type="checkbox"
            checked={istTestkonto}
            onChange={(e) => setIstTestkonto(e.target.checked)}
          />
          <span>
            <strong>Testkonto</strong>
            <span className="meta">
              {' '}
              — zum Ausprobieren. Erzeugt echte Fahrten und Spesentage und
              gehört vor dem Pilotstart entfernt.
            </span>
          </span>
        </label>

        <div className="reihe reihe--rechts">
          <Knopf art="still" onClick={eigenschaften.fertig}>
            Abbrechen
          </Knopf>
          <Knopf
            art="haupt"
            disabled={!vollstaendig || laeuft}
            onClick={() => void speichern()}
          >
            {laeuft ? 'Wird angelegt …' : istTestkonto ? 'Testkonto anlegen' : 'Anlegen'}
          </Knopf>
        </div>
      </div>
    </Karte>
  );
}
